import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { todayISO, JOB_STATUS_LABEL } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { trackCreate, useOpenedGoal } from "@/lib/metrica";
import { MOBILE_STICKY_FOOTER } from "@/lib/utils";
import {
  InvoiceItemsEditor, serializeInvoiceItems, makeEmptyInvoiceItem, validateInvoiceItems,
} from "@/components/InvoiceItemsEditor";
import { BillingChoice } from "@/components/BillingChoice";

/**
 * Shared "New / Edit Job" dialog — the ONLY place in the app that
 * knows how to POST a job (and, when Faturalı is selected, chain a
 * POST /invoices right after).  Consumed by both:
 *
 *   * Jobs.jsx           — free customer picker (customers prop)
 *   * CustomerDetail.jsx — customer prop locked to the current cari
 *                          (dropdown hidden, customer_id auto-injected)
 *
 * Contract:
 *   - Pass EITHER ``customers`` (dropdown mode) OR ``lockedCustomer``
 *     (implicit id mode).  Never both.
 *   - Edit flow (``editing`` non-null) NEVER emits the inline invoice;
 *     invoice CRUD lives on the Faturalar page as the single source of
 *     truth for per-invoice edits.
 */
const EMPTY = {
  customer_id: "", name: "", description: "", amount: 0,
  status: "active", start_date: todayISO(), end_date: "",
  due_date: "", invoice_status: "invoiced", cash_account: "company", notes: "",
  invoice_date: todayISO(),
  invoice_number: "",
  invoice_items: null,
};

export default function JobFormDialog({
  open, onOpenChange, editing, customers, lockedCustomer, onSaved, defaultCustomerId,
}) {
  const [form, setForm] = useState(EMPTY);
  useOpenedGoal("job_create_opened", open && !editing);
  const [vatRates, setVatRates] = useState([]);
  // Edit + faturalı akışta yüklenen mevcut satış faturaları.
  // Tek fatura (length===1) → aynı invoice PUT ile güncellenir (yeni
  // duplicate oluşmaz).  Birden fazla fatura (length>1) → kullanıcı
  // Faturalar sayfasına yönlendirilir; iş formu 1:N faturayı tek
  // InvoiceItemsEditor'da birleştirmez.  Boş (length===0) → info
  // mesajı gösterilir; yine Faturalar sayfası önerilir.
  const [existingInvoices, setExistingInvoices] = useState([]);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/vat-rates?status=active")
      .then((r) => setVatRates(r.data || []))
      .catch(() => setVatRates([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        ...EMPTY, ...editing,
        end_date: editing.end_date || "",
        due_date: editing.due_date || "",
      });
    } else {
      setForm({
        ...EMPTY,
        customer_id: lockedCustomer?.id || defaultCustomerId || "",
        // Yeni işte faturalandırma seçimi kullanıcıya bırakılır (zorunlu).
        invoice_status: "",
      });
    }
    setExistingInvoices([]);
    setEditingInvoiceId(null);
    // defaultCustomerId yalnızca dialog açılışında okunur; değişimi formu sıfırlamamalı.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, lockedCustomer]);

  // Edit + Faturalı akışta iş üzerindeki mevcut satış faturalarını çek.
  // Bu tetikleme, kullanıcı dialog'u açtığında ve invoice_status
  // "invoiced" iken çalışır; kullanıcı sonradan Faturalı→Faturasız
  // yaparsa yeniden fetch etmemek için form.invoice_status DEĞİL
  // editing.invoice_status'e bakarız.
  useEffect(() => {
    if (!open || !editing) return;
    if ((editing.invoice_status || "invoiced") !== "invoiced") return;
    let cancelled = false;
    setLoadingInvoices(true);
    api.get(`/invoices?type=sale&job_id=${encodeURIComponent(editing.id)}`)
      .then((r) => {
        if (cancelled) return;
        const list = r.data || [];
        setExistingInvoices(list);
        // Tek fatura varsa otomatik aç.  Kalemleri form.invoice_items'a
        // yükle; toplamlar canlı yeniden hesaplanır ve İş Bedeli input'u
        // subtotal_ex_vat'ı gösterir.
        if (list.length === 1) {
          const inv = list[0];
          setEditingInvoiceId(inv.id);
          setForm((f) => ({
            ...f,
            invoice_date: inv.invoice_date || todayISO(),
            invoice_number: inv.invoice_number || "",
            invoice_items: (inv.items || []).map((it) => ({
              description: it.description || "",
              quantity: String(it.quantity ?? 1),
              unit_price: String(it.unit_price ?? 0),
              vat_rate: String(it.vat_rate ?? 20),
            })),
          }));
        }
      })
      .catch(() => { if (!cancelled) setExistingInvoices([]); })
      .finally(() => { if (!cancelled) setLoadingInvoices(false); });
    return () => { cancelled = true; };
  }, [open, editing]);

  const displayedCustomers = useMemo(() => customers ?? [], [customers]);

  // Faturalı akışta İş Bedeli = SUM(kalem.q * kalem.u).  Bu tek
  // source-of-truth'tur; kullanıcının manuel girdiği ``form.amount``
  // yalnızca Faturasız akışta anlamlıdır.  Faturalı → Faturasız
  // geçişinde manuel alan tekrar editable olur ve fatura kalemlerinden
  // gelen otomatik değer manuel state'e taşınmaz (kullanıcı kendi
  // değerini serbestçe girer).
  const invoiceSubtotalExVat = useMemo(() => {
    if (!Array.isArray(form.invoice_items)) return 0;
    return form.invoice_items.reduce(
      (s, it) => s + (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0),
      0,
    );
  }, [form.invoice_items]);

  // Faturalı akışta inline invoice görünür — üç durum:
  //   * CREATE + faturalı              → yeni fatura oluşturulur
  //   * EDIT + faturalı + 1 fatura     → mevcut invoice PUT güncellenir
  //   * EDIT + faturalı + N fatura     → info mesajı (aşağıda ayrı UI)
  //   * EDIT + faturalı + 0 fatura     → info mesajı (Faturalar'dan ekle)
  const isInvoicedFlow =
    form.invoice_status === "invoiced" &&
    (!editing || (editing && existingInvoices.length === 1));
  const isInvoicedCreate = !editing && form.invoice_status === "invoiced";
  // Yeni işte mobil sıralama: Müşteri, İş Adı, Faturalandırma, (Fatura Bilgileri), sonra diğerleri.
  const mTop = editing ? "" : "max-md:order-[-2]";
  const isInvoicedSingleEdit =
    !!editing && form.invoice_status === "invoiced" && existingInvoices.length === 1;
  const showMultiInvoiceInfo =
    !!editing && form.invoice_status === "invoiced" &&
    existingInvoices.length > 1;
  const showNoInvoiceInfo =
    !!editing && form.invoice_status === "invoiced" &&
    !loadingInvoices && existingInvoices.length === 0;

  const onSubmit = async (e) => {
    e.preventDefault();
    const custId = lockedCustomer?.id || form.customer_id;
    if (!custId) { toast.error("Müşteri seçin"); return; }
    if (!editing && !form.invoice_status) { toast.error("Faturalandırma seçin: Faturalı veya Faturasız"); return; }

    // Inline fatura akışı üç senaryoyu kapsar:
    //   * CREATE + faturalı            → POST /invoices (yeni)
    //   * EDIT + faturalı + 1 fatura   → PUT /invoices/{id} (mevcut)
    //   * EDIT + faturalı + N/0 fatura → invoice submit atlanır
    // Yeni faturalı işte kalem doğrulaması zorunlu; düzenlemede yalnızca
    // kullanıcı tek faturanın kalemlerini gerçekten girdiyse uygulanır.
    const wantsInlineInvoice =
      form.invoice_status === "invoiced" &&
      (isInvoicedCreate || (isInvoicedSingleEdit &&
        Array.isArray(form.invoice_items) &&
        form.invoice_items.length > 0 &&
        form.invoice_items.some((it) => parseFloat(it.unit_price) > 0)));
    if (wantsInlineInvoice) {
      if (!form.invoice_date) { toast.error("Fatura tarihini girin"); return; }
      const itemsErr = validateInvoiceItems(form.invoice_items, "job-invoice-item");
      if (itemsErr) { toast.error(itemsErr); return; }
    }

    try {
      const {
        invoice_date: _invDate, invoice_number: _invNo,
        invoice_items: _invItems, ...jobBody
      } = form;
      // Güvenlik: faturalı akışta job.amount = SUM(kalem KDV hariç).
      // Kullanıcının manuel amount state'i (UI'da zaten disabled) sunucuya
      // asla gönderilmez; her zaman kalemlerden hesaplanmış değer gider.
      // Yeni faturalı işte Faturasız iken yazılmış manuel bedel asla gönderilmez.
      const effectiveAmount = (wantsInlineInvoice || isInvoicedCreate)
        ? parseFloat(invoiceSubtotalExVat.toFixed(2))
        : (parseFloat(form.amount) || 0);
      const payload = {
        ...jobBody,
        customer_id: custId,
        amount: effectiveAmount,
        end_date: form.end_date || null,
        due_date: form.due_date || null,
      };
      let jobId;
      if (editing) {
        await api.put(`/jobs/${editing.id}`, payload);
        jobId = editing.id;
      } else {
        const { data } = await trackCreate("job", () => api.post("/jobs", payload));
        jobId = data.id;
      }

      if (wantsInlineInvoice && jobId) {
        try {
          const invoicePayload = {
            type: "sale",
            customer_id: custId,
            job_id: jobId,
            invoice_date: form.invoice_date,
            invoice_number: (form.invoice_number || "").trim(),
            items: serializeInvoiceItems(form.invoice_items),
          };
          if (isInvoicedSingleEdit && editingInvoiceId) {
            // Mevcut invoice güncellenir — YENİ invoice oluşturulmaz.
            // Aynı id üzerinde PUT çalışır; invoice-summary, müşteri
            // cari alacağı ve KDV raporu backend derived hesapları
            // üzerinden otomatik yeniden hesaplanır.
            await api.put(`/invoices/${editingInvoiceId}`, invoicePayload);
            toast.success("İş ve satış faturası güncellendi");
          } else {
            await api.post("/invoices", invoicePayload);
            toast.success("İş ve satış faturası oluşturuldu");
          }
        } catch (err) {
          toast.error(
            (isInvoicedSingleEdit
              ? "İş güncellendi ancak fatura güncellenemedi: "
              : "İş kaydedildi ancak fatura oluşturulamadı: ") +
            (err?.response?.data?.detail || "Fatura sonradan düzenlenmelidir"),
          );
        }
      } else {
        toast.success(editing ? "İş güncellendi" : "İş eklendi");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "İşlem başarısız");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">
            {editing ? "İş Düzenle" : "Yeni İş"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4"
              data-testid="job-form">

          {/* Customer picker OR locked cari readout */}
          {lockedCustomer ? (
            <div className={`md:col-span-2 flex items-center gap-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 ${mTop}`}
                 data-testid="job-customer-locked">
              <span className="uppercase tracking-wider">Müşteri</span>
              <span className="font-medium text-slate-800">
                {lockedCustomer.name}{lockedCustomer.company_name ? ` — ${lockedCustomer.company_name}` : ""}
              </span>
            </div>
          ) : (
            <div className={`space-y-1.5 md:col-span-2 ${mTop}`}>
              <Label className="text-xs">Müşteri *</Label>
              <Select value={form.customer_id}
                      onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                <SelectTrigger className="h-10 rounded-lg" data-testid="job-customer-select">
                  <SelectValue placeholder="Seçin" />
                </SelectTrigger>
                <SelectContent>
                  {displayedCustomers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <Field label="İş Adı *" value={form.name}
                 onChange={(v) => setForm({ ...form, name: v })}
                 required testId="job-name-input" className={mTop} />
          {!editing && (
            <BillingChoice className={mTop} value={form.invoice_status}
                           onChange={(v) => setForm({ ...form, invoice_status: v })} />
          )}
          <div className="space-y-1.5">
            <Label className="text-xs">
              {isInvoicedFlow
                ? "İş Bedeli (₺, KDV Hariç — kalemlerden otomatik)"
                : "İş Bedeli (₺) *"}
            </Label>
            <Input
              type="number" step="0.01"
              value={isInvoicedFlow
                ? invoiceSubtotalExVat.toFixed(2)
                : (form.amount ?? "")}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              disabled={isInvoicedFlow}
              required={!isInvoicedFlow}
              className="h-10 rounded-lg"
              data-testid="job-amount-input"
            />
            {isInvoicedCreate && (
              <p className="md:hidden text-[11px] text-slate-500" data-testid="job-amount-auto-hint">
                Fiyatı yukarıdaki Fatura Kalemleri'nden girin; bu alan otomatik hesaplanır.
              </p>
            )}
          </div>
          <Field label="Başlangıç" type="date" value={form.start_date}
                 onChange={(v) => setForm({ ...form, start_date: v })} />
          <Field label="Teslim" type="date" value={form.end_date}
                 onChange={(v) => setForm({ ...form, end_date: v })} />
          <Field label="Vade" type="date" value={form.due_date}
                 onChange={(v) => setForm({ ...form, due_date: v })} />
          <div className="space-y-1.5">
            <Label className="text-xs">Durum</Label>
            <Select value={form.status}
                    onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger className="h-10 rounded-lg" data-testid="job-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(JOB_STATUS_LABEL ?? {})
                  .filter(([k]) => editing || k !== "cancelled")
                  .map(([k, l]) => (
                  <SelectItem key={k} value={k}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {editing && (<div className="space-y-1.5">
            <Label className="text-xs">Fatura Durumu</Label>
            <Select value={form.invoice_status}
                    onValueChange={(v) => setForm({ ...form, invoice_status: v })}>
              <SelectTrigger className="h-10 rounded-lg" data-testid="job-invoice-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="invoiced">Faturalı</SelectItem>
                <SelectItem value="non_invoiced">Faturasız</SelectItem>
              </SelectContent>
            </Select>
          </div>)}
          <div className="md:col-span-2 space-y-1.5">
            <Label className="text-xs">Açıklama</Label>
            <Input value={form.description}
                   onChange={(e) => setForm({ ...form, description: e.target.value })}
                   className="h-10 rounded-lg" />
          </div>

          {isInvoicedFlow && (
            <div className={`md:col-span-2 space-y-3 border border-slate-200 rounded-xl p-4 bg-slate-50/50 ${editing ? "" : "max-md:order-[-1] max-md:p-3"}`}
                 data-testid="job-invoice-section">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-slate-800">
                  {isInvoicedSingleEdit ? "Mevcut Fatura" : "Fatura Bilgileri"}
                </div>
                {isInvoicedSingleEdit && (
                  <span className="text-[11px] text-slate-500" data-testid="job-invoice-editing-badge">
                    Bu fatura güncellenecek — yeni fatura oluşturulmaz.
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Fatura Tarihi *" type="date"
                       value={form.invoice_date}
                       onChange={(v) => setForm({ ...form, invoice_date: v })}
                       testId="job-invoice-date-input" />
                <Field label="Fatura No" value={form.invoice_number || ""}
                       onChange={(v) => setForm({ ...form, invoice_number: v })}
                       testId="job-invoice-number-input" />
                <div className="text-[11px] text-slate-500 self-end pb-2">
                  Müşteri: iş kaydından otomatik alınır.
                </div>
              </div>

              <InvoiceItemsEditor
                items={form.invoice_items && form.invoice_items.length
                  ? form.invoice_items
                  : [makeEmptyInvoiceItem(vatRates)]}
                setItems={(next) => setForm({ ...form, invoice_items: next })}
                vatRates={vatRates}
                testIdPrefix="job-invoice-item"
                mobileCards={!editing}
              />

              <p className="text-[11px] text-slate-500 leading-relaxed">
                {isInvoicedSingleEdit
                  ? "Kalem, tarih veya fatura numarasında yaptığınız değişiklikler mevcut fatura üzerine kaydedilir. İş Bedeli otomatik olarak KDV hariç toplama eşitlenir; müşteri cari alacağı ve KDV raporu backend hesapları üzerinden güncellenir."
                  : "Bu fatura, işin ilk satış faturası olarak kaydedilir. Bir işe sonradan birden fazla fatura kesilebilir."}
              </p>
            </div>
          )}

          {showMultiInvoiceInfo && (
            <div className="md:col-span-2 space-y-2 border border-amber-200 rounded-xl p-4 bg-amber-50/60"
                 data-testid="job-multi-invoice-info">
              <div className="text-sm font-medium text-amber-900">
                Bu işe bağlı {existingInvoices.length} fatura bulunuyor
              </div>
              <p className="text-[11px] text-amber-800 leading-relaxed">
                Bir işe birden fazla fatura kesildiğinde bu form kalemleri
                birleştirmez. Her faturayı ayrı ayrı düzenlemek için
                Faturalar sayfasını kullanın.
              </p>
              <ul className="text-xs text-amber-900 space-y-1 pl-4 list-disc">
                {existingInvoices.map((inv) => (
                  <li key={inv.id} data-testid={`job-multi-invoice-row-${inv.id}`}>
                    <span className="font-medium">
                      {inv.invoice_number || "(No yok)"}
                    </span>
                    {" · "}
                    <span>{inv.invoice_date}</span>
                    {" · "}
                    <span>₺{Number(inv.grand_total || 0).toLocaleString("tr-TR")}</span>
                  </li>
                ))}
              </ul>
              <a
                href="/invoices"
                className="inline-flex items-center gap-1 text-xs font-medium text-amber-900 hover:underline mt-1"
                data-testid="job-goto-invoices-link"
              >
                Faturalar sayfasına git →
              </a>
            </div>
          )}

          {showNoInvoiceInfo && (
            <div className="md:col-span-2 space-y-2 border border-slate-200 rounded-xl p-4 bg-slate-50/50"
                 data-testid="job-no-invoice-info">
              <div className="text-sm font-medium text-slate-800">
                Bu iş faturalı görünüyor ancak henüz kesilmiş fatura yok
              </div>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                Fatura eklemek için Faturalar sayfasını kullanabilirsiniz.
                Fatura Durumunu "Faturasız" olarak değiştirirseniz İş Bedeli
                manuel girilebilir.
              </p>
              <a
                href="/invoices"
                className="inline-flex items-center gap-1 text-xs font-medium text-slate-800 hover:underline mt-1"
                data-testid="job-goto-invoices-link-empty"
              >
                Faturalar sayfasına git →
              </a>
            </div>
          )}

          {!editing && (
            <div className="md:col-span-2 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              Tahsilatları sonra her satırdaki {`«Tahsilatlar»`} butonundan ekleyebilirsiniz.
            </div>
          )}

          <DialogFooter className={`md:col-span-2 max-md:order-last ${MOBILE_STICKY_FOOTER}`} data-testid="job-form-footer">
            <Button type="button" variant="outline"
                    onClick={() => onOpenChange(false)}>Vazgeç</Button>
            <Button type="submit" className="bg-slate-900 hover:bg-slate-800"
                    data-testid="job-save-button">Kaydet</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, required, type = "text", step, testId, className = "" }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-xs">{label}</Label>
      <Input value={value ?? ""} type={type} step={step}
             onChange={(e) => onChange(e.target.value)}
             required={required} className="h-10 rounded-lg"
             data-testid={testId} />
    </div>
  );
}
